import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getAllCategories, getHotelMenu } from "../../../api/user/hotels";

export default function GuestMenu() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const hotelId = params.get("hotel");
  const roomId = params.get("roomId") || params.get("room");

  const [openCategoryId, setOpenCategoryId] = useState(null);
  const [page, setPage] = useState(1);
  const [countMap, setCountMap] = useState({});
  const [notesMap, setNotesMap] = useState({});
  const [cartItems, setCartItems] = useState([]);

  const { data: categoriesRes } = useQuery({
    queryKey: ["guest-hotel-menu-categories", hotelId],
    queryFn: () => getAllCategories(hotelId),
    enabled: !!hotelId,
    keepPreviousData: true,
  });

  const categories = categoriesRes?.data?.categories || [];

  useEffect(() => {
    if (!openCategoryId && categories?.[0]?._id) {
      setOpenCategoryId(categories[0]._id);
    }
  }, [categories, openCategoryId]);

  const { data: itemsRes } = useQuery({
    queryKey: ["guest-hotel-menu-items", openCategoryId, page],
    queryFn: () => getHotelMenu(openCategoryId, page, 10),
    enabled: !!openCategoryId,
    keepPreviousData: true,
  });

  const items = itemsRes?.data?.items || [];
  const pagination = itemsRes?.data?.pagination || {};

  const handleAddOrUpdate = (itemId, quantity) => {
    const note = notesMap[itemId] || "";
    setCountMap((prev) => ({ ...prev, [itemId]: quantity }));
    setCartItems((prev) => {
      if (quantity === 0) return prev.filter((i) => i.itemId !== itemId);
      const idx = prev.findIndex((i) => i.itemId === itemId);
      const updated = { itemId, quantity, note };
      if (idx !== -1) {
        const copy = [...prev];
        copy[idx] = updated;
        return copy;
      }
      return [...prev, updated];
    });
  };

  const handleProceed = () => {
    navigate("/hotel-cart-guest", {
      state: { hotelId, roomId, items: cartItems },
    });
  };

  return (
    <div className="max-w-6xl mx-auto p-6 mt-8 mb-8">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Menu</h1>
        <button onClick={() => navigate(-1)} className="text-[#4CAF50]">
          Back
        </button>
      </div>

      <div className="grid md:grid-cols-[240px_1fr] gap-4">
        <div className="bg-white border rounded p-3 space-y-2">
          {categories.map((c) => (
            <button
              key={c._id}
              className={`block w-full text-left px-3 py-2 rounded ${
                openCategoryId === c._id ? "bg-[#ECF8ED]" : "bg-white"
              }`}
              onClick={() => {
                setOpenCategoryId(c._id);
                setPage(1);
              }}
            >
              {c.name}
            </button>
          ))}
        </div>

        <div className="bg-white border rounded p-3">
          <div className="grid sm:grid-cols-2 grid-cols-1 gap-3">
            {items.map((it) => (
              <div key={it._id} className="border rounded p-3">
                <p className="font-medium">{it.itemName}</p>
                <p className="text-sm text-gray-600">₾{it.price}</p>
                <textarea
                  placeholder="Notes"
                  className="mt-2 w-full border rounded p-2 text-sm"
                  value={notesMap[it._id] || ""}
                  onChange={(e) =>
                    setNotesMap((prev) => ({ ...prev, [it._id]: e.target.value }))
                  }
                />
                <div className="flex items-center gap-2 mt-2">
                  <button
                    className="px-3 py-1 border rounded"
                    onClick={() =>
                      handleAddOrUpdate(it._id, Math.max(0, (countMap[it._id] || 0) - 1))
                    }
                  >
                    -
                  </button>
                  <span>{countMap[it._id] || 0}</span>
                  <button
                    className="px-3 py-1 border rounded"
                    onClick={() => handleAddOrUpdate(it._id, (countMap[it._id] || 0) + 1)}
                  >
                    +
                  </button>
                </div>
              </div>
            ))}
          </div>

          {pagination?.totalPages > 1 && (
            <div className="flex justify-end mt-4 gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1 border rounded"
              >
                Prev
              </button>
              <button
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 border rounded"
              >
                Next
              </button>
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <button
              disabled={!cartItems.length}
              onClick={handleProceed}
              className="bg-[#4CAF50] disabled:opacity-50 text-white font-bold py-2 px-4 rounded"
            >
              Review Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


